/**========================================================================
 *
 *                    DATE RECURRENCE GENERATOR
 *
 * Pure functions for generating recurring event date series.
 * All dates are generated in local business time and converted to UTC.
 * No external recurrence libraries — supports daily, weekly, monthly.
 *
 *========================================================================**/

import {
  localize_utc_date,
  create_utc_timestamp_that_appears_as_local_midnight,
  get_utc_offset,
} from '#lib/date_utils.js';

export const MAX_OCCURRENCES = 730;

/**
 * Generates an array of date pairs for recurring event occurrences.
 *
 * @param {{ starts_at: string, ends_at: string, all_day: boolean }} base_event - The base event with UTC ISO dates
 * @param {{ frequency: 'daily'|'weekly'|'monthly', interval: number, end_date: string }} pattern - Recurrence pattern (end_date is local YYYY-MM-DD)
 * @param {string} timezone - IANA timezone string (e.g., 'Europe/Berlin')
 * @returns {Array<{ starts_at: string, ends_at: string, series_index: number }>} Generated UTC ISO date pairs
 */
export function generate_series_dates(base_event, pattern, timezone) {
  const { starts_at, ends_at } = base_event;
  const { frequency, interval = 1, end_date } = pattern;

  if (!starts_at || !ends_at || !frequency || !end_date) {
    throw new Error('generate_series_dates: missing required parameters');
  }

  // Guard against client-supplied patterns: interval 0 would never advance
  // the date and materialize MAX_OCCURRENCES identical rows
  if (!Number.isInteger(interval) || interval < 1) {
    throw new Error('generate_series_dates: interval must be a positive integer');
  }

  // Calculate event duration in milliseconds
  const duration_ms = new Date(ends_at).getTime() - new Date(starts_at).getTime();

  // Convert base starts_at to local time components
  const local_start = localize_utc_date(new Date(starts_at), timezone);
  const local_hours = local_start.getUTCHours();
  const local_minutes = local_start.getUTCMinutes();
  const local_seconds = local_start.getUTCSeconds();
  const local_time_offset_ms = ((local_hours * 60 + local_minutes) * 60 + local_seconds) * 1000;

  // Extract the base local date
  let current_year = local_start.getUTCFullYear();
  let current_month = local_start.getUTCMonth(); // 0-based
  let current_day = local_start.getUTCDate();
  const base_day = current_day; // Remember original day for monthly clamping

  // Parse end_date as local boundary
  const [end_year, end_month_1, end_day] = end_date.split('-').map(Number);

  const results = [];

  for (let i = 1; i <= MAX_OCCURRENCES; i++) {
    // Advance the date
    switch (frequency) {
      case 'daily':
        current_day += interval;
        break;
      case 'weekly':
        current_day += 7 * interval;
        break;
      case 'monthly':
        current_month += interval;
        // Clamp day to last day of target month
        current_day = clamp_day_to_month(base_day, current_year, current_month);
        break;
      default:
        throw new Error(`generate_series_dates: unsupported frequency "${frequency}"`);
    }

    // Normalize via Date constructor (handles day overflow into next month)
    const normalized = new Date(Date.UTC(current_year, current_month, current_day));
    current_year = normalized.getUTCFullYear();
    current_month = normalized.getUTCMonth();
    current_day = normalized.getUTCDate();

    // Check if we've passed the end date (local comparison)
    if (current_year > end_year ||
        (current_year === end_year && current_month > end_month_1 - 1) ||
        (current_year === end_year && current_month === end_month_1 - 1 && current_day > end_day)) {
      break;
    }

    // Build the local date string
    const local_date_string = `${current_year}-${String(current_month + 1).padStart(2, '0')}-${String(current_day).padStart(2, '0')}`;

    // Convert to UTC: get midnight in local tz, then add the local time offset
    const midnight_utc = create_utc_timestamp_that_appears_as_local_midnight(local_date_string, timezone);

    // For the starts_at, add the local time offset to midnight
    const starts_at_utc = new Date(midnight_utc.getTime() + local_time_offset_ms);

    // Verify the local time is correct after DST conversion by checking the offset
    // at the actual target time (not at midnight)
    const offset_at_target = get_utc_offset(timezone, starts_at_utc);
    const offset_at_midnight = get_utc_offset(timezone, midnight_utc);
    if (offset_at_target !== offset_at_midnight) {
      // DST transition happened between midnight and the event time — adjust
      starts_at_utc.setTime(starts_at_utc.getTime() - (offset_at_target - offset_at_midnight) * 60 * 1000);
    }

    const ends_at_utc = new Date(starts_at_utc.getTime() + duration_ms);

    results.push({
      starts_at: starts_at_utc.toISOString(),
      ends_at: ends_at_utc.toISOString(),
      series_index: i,
    });
  }

  return results;
}

/**
 * Clamps a day-of-month to the last valid day of the target month.
 * Handles months with fewer days (e.g., 31 → 28 for February).
 *
 * @param {number} desired_day - The original day of month
 * @param {number} year - Target year
 * @param {number} month - Target month (0-based, may be >11 — that's fine, Date handles overflow)
 * @returns {number} Clamped day
 */
function clamp_day_to_month(desired_day, year, month) {
  // Day 0 of next month = last day of target month
  const days_in_month = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return Math.min(desired_day, days_in_month);
}

/**
 * Validates a series pattern and returns occurrence count.
 *
 * @param {{ frequency: string, interval?: number, end_date: string }} pattern
 * @param {string} base_starts_at - UTC ISO string of the base event start
 * @param {string} timezone - IANA timezone string
 * @returns {{ valid: boolean, error_key?: string, occurrence_count: number }}
 */
export function validate_series_pattern(pattern, base_starts_at, timezone) {
  const { frequency, interval = 1, end_date } = pattern || {};

  // Validate frequency
  if (!['daily', 'weekly', 'monthly'].includes(frequency)) {
    return { valid: false, error_key: 'series_invalid_frequency', occurrence_count: 0 };
  }

  // Validate interval
  if (!Number.isInteger(interval) || interval < 1) {
    return { valid: false, error_key: 'series_invalid_interval', occurrence_count: 0 };
  }

  // Validate end_date format
  if (!end_date || !/^\d{4}-\d{2}-\d{2}$/.test(end_date)) {
    return { valid: false, error_key: 'series_invalid_end_date', occurrence_count: 0 };
  }

  // Validate end_date is after base event
  const local_start = localize_utc_date(new Date(base_starts_at), timezone);
  const start_date_string = `${local_start.getUTCFullYear()}-${String(local_start.getUTCMonth() + 1).padStart(2, '0')}-${String(local_start.getUTCDate()).padStart(2, '0')}`;
  if (end_date <= start_date_string) {
    return { valid: false, error_key: 'series_end_before_start', occurrence_count: 0 };
  }

  // Validate end_date is not beyond end of next calendar year
  const now = new Date();
  const max_year = now.getFullYear() + 1;
  const [end_year] = end_date.split('-').map(Number);
  if (end_year > max_year) {
    return { valid: false, error_key: 'series_end_date_too_far', occurrence_count: 0 };
  }

  // Compute occurrence count
  try {
    const dates = generate_series_dates(
      { starts_at: base_starts_at, ends_at: base_starts_at, all_day: false },
      pattern,
      timezone
    );
    const occurrence_count = dates.length + 1; // +1 for the base event

    if (occurrence_count > MAX_OCCURRENCES) {
      return { valid: false, error_key: 'series_too_many_occurrences', occurrence_count };
    }

    return { valid: true, occurrence_count };
  } catch {
    return { valid: false, error_key: 'series_generation_error', occurrence_count: 0 };
  }
}
